import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Key, Plus, Copy, Trash2, RefreshCw, Eye, EyeOff, 
  Webhook, CheckCircle, XCircle, Clock, ExternalLink,
  Code, FileJson, Book
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const WEBHOOK_EVENTS = [
  { id: 'employee.imported', label: 'Employee Imported' },
  { id: 'case.created', label: 'Case Created' },
  { id: 'case.updated', label: 'Case Updated' },
  { id: 'case.completed', label: 'Case Completed' },
  { id: 'verification.started', label: 'Verification Started' },
  { id: 'verification.completed', label: 'Verification Completed' },
  { id: 'insufficiency.raised', label: 'Insufficiency Raised' },
  { id: 'insufficiency.resolved', label: 'Insufficiency Resolved' },
  { id: 'report.generated', label: 'Report Generated' },
];

export default function ExternalIntegrationPage({ user }) {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDocsDialog, setShowDocsDialog] = useState(false);
  const [newKeyData, setNewKeyData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    webhook_url: '',
    webhook_events: ['case.completed', 'verification.completed', 'insufficiency.raised'],
    rate_limit: 1000
  });

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/external/api-keys`, { headers });
      setApiKeys(res.data);
    } catch (error) {
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/external/api-keys`, formData, { headers });
      setNewKeyData(res.data);
      fetchApiKeys();
      toast.success('API key created successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create API key');
    }
  };

  const handleRevokeKey = async (keyId) => {
    if (!confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) return;
    
    try {
      await axios.delete(`${API_URL}/api/external/api-keys/${keyId}`, { headers });
      fetchApiKeys();
      toast.success('API key revoked');
    } catch (error) {
      toast.error('Failed to revoke API key');
    }
  };

  const handleRegenerateSecret = async (keyId) => {
    try {
      const res = await axios.post(`${API_URL}/api/external/api-keys/${keyId}/regenerate-webhook-secret`, {}, { headers });
      toast.success('Webhook secret regenerated');
      // Show the new secret
      alert(`New Webhook Secret:\n\n${res.data.webhook_secret}\n\nSave this secret - it cannot be retrieved again.`);
    } catch (error) {
      toast.error('Failed to regenerate secret');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const toggleEvent = (eventId) => {
    setFormData(prev => ({
      ...prev,
      webhook_events: prev.webhook_events.includes(eventId)
        ? prev.webhook_events.filter(e => e !== eventId)
        : [...prev.webhook_events, eventId]
    }));
  };

  return (
    <div className="p-6 space-y-6 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">External Integration API</h1>
          <p className="text-slate-600">Connect HRMIS, CRM, Zoho Creator, and other systems</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowDocsDialog(true)}>
            <Book className="w-4 h-4 mr-2" /> API Documentation
          </Button>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="w-4 h-4 mr-2" /> Create API Key
          </Button>
        </div>
      </div>

      {/* Quick Start Guide */}
      <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
              <Code className="w-6 h-6 text-indigo-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-indigo-900">Quick Start</h3>
              <p className="text-sm text-indigo-700 mt-1">
                1. Create an API key below<br />
                2. Use the key in your HTTP header: <code className="bg-indigo-100 px-1 rounded">X-API-Key: your_key</code><br />
                3. POST to <code className="bg-indigo-100 px-1 rounded">/api/external/v1/cases</code> to create verification requests
              </p>
            </div>
            <Button variant="outline" size="sm" className="border-indigo-300 text-indigo-700" asChild>
              <a href={`${API_URL}/api/external/v1/docs`} target="_blank" rel="noopener noreferrer">
                <FileJson className="w-4 h-4 mr-2" /> View Full Docs
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* API Keys Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5" /> API Keys
          </CardTitle>
          <CardDescription>Manage API keys for external system integrations</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-500">Loading...</div>
          ) : apiKeys.length === 0 ? (
            <div className="text-center py-8">
              <Key className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No API keys created yet</p>
              <Button className="mt-4" onClick={() => setShowCreateDialog(true)}>
                <Plus className="w-4 h-4 mr-2" /> Create Your First API Key
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Key Prefix</TableHead>
                  <TableHead>Permissions</TableHead>
                  <TableHead>Webhook</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Used</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {apiKeys.map(key => (
                  <TableRow key={key.id}>
                    <TableCell className="font-medium">{key.name}</TableCell>
                    <TableCell>
                      <code className="bg-slate-100 px-2 py-1 rounded text-sm">{key.key_prefix}...</code>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {key.permissions?.map(p => (
                          <Badge key={p} variant="secondary" className="text-xs">{p}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      {key.webhook_url ? (
                        <Badge variant="outline" className="text-green-600 border-green-300">
                          <Webhook className="w-3 h-3 mr-1" /> Configured
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-slate-400">Not set</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {key.is_active ? (
                        <Badge className="bg-green-100 text-green-700">Active</Badge>
                      ) : (
                        <Badge variant="destructive">Revoked</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {key.last_used_at ? new Date(key.last_used_at).toLocaleDateString() : 'Never'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {key.webhook_url && (
                          <Button size="sm" variant="ghost" onClick={() => handleRegenerateSecret(key.id)} title="Regenerate webhook secret">
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleRevokeKey(key.id)} title="Revoke key">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create API Key Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create API Key</DialogTitle>
            <DialogDescription>Generate a new API key for external system integration</DialogDescription>
          </DialogHeader>
          
          {newKeyData ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-green-700 font-medium mb-2">
                  <CheckCircle className="w-5 h-5" /> API Key Created
                </div>
                <p className="text-sm text-green-600 mb-3">
                  Save this key now - it cannot be retrieved again!
                </p>
                <div className="bg-white border rounded p-3 font-mono text-sm break-all">
                  {newKeyData.api_key}
                </div>
                <Button size="sm" variant="outline" className="mt-2" onClick={() => copyToClipboard(newKeyData.api_key)}>
                  <Copy className="w-4 h-4 mr-2" /> Copy Key
                </Button>
              </div>
              
              {newKeyData.webhook_secret && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="font-medium text-amber-700 mb-2">Webhook Secret</div>
                  <div className="bg-white border rounded p-3 font-mono text-sm break-all">
                    {newKeyData.webhook_secret}
                  </div>
                  <Button size="sm" variant="outline" className="mt-2" onClick={() => copyToClipboard(newKeyData.webhook_secret)}>
                    <Copy className="w-4 h-4 mr-2" /> Copy Secret
                  </Button>
                </div>
              )}
              
              <DialogFooter>
                <Button onClick={() => { setShowCreateDialog(false); setNewKeyData(null); setFormData({ name: '', webhook_url: '', webhook_events: ['case.completed', 'verification.completed'], rate_limit: 1000 }); }}>
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label>Integration Name *</Label>
                <Input 
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Zoho Creator Production"
                />
              </div>
              
              <div>
                <Label>Webhook URL (Optional)</Label>
                <Input 
                  value={formData.webhook_url}
                  onChange={(e) => setFormData({...formData, webhook_url: e.target.value})}
                  placeholder="https://your-system.com/webhook"
                />
                <p className="text-xs text-slate-500 mt-1">We'll send status updates to this URL</p>
              </div>
              
              {formData.webhook_url && (
                <div>
                  <Label className="mb-2 block">Webhook Events</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {WEBHOOK_EVENTS.map(event => (
                      <div key={event.id} className="flex items-center gap-2">
                        <Checkbox 
                          id={event.id}
                          checked={formData.webhook_events.includes(event.id)}
                          onCheckedChange={() => toggleEvent(event.id)}
                        />
                        <label htmlFor={event.id} className="text-sm cursor-pointer">{event.label}</label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                <Button onClick={handleCreateKey} disabled={!formData.name}>Create API Key</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* API Documentation Dialog */}
      <Dialog open={showDocsDialog} onOpenChange={setShowDocsDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>API Documentation v2.0</DialogTitle>
            <DialogDescription>External Integration API for HRMIS, CRM, Zoho Creator and other systems</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 text-sm">
            <div>
              <h3 className="font-semibold text-lg mb-2">Authentication</h3>
              <p className="text-slate-600 mb-2">Include your API key in the request header:</p>
              <code className="block bg-slate-100 p-3 rounded">X-API-Key: jv_live_your_api_key_here</code>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg mb-2">Base URL</h3>
              <code className="block bg-slate-100 p-3 rounded">{API_URL}/api/external/v1</code>
            </div>
            
            {/* Employee Import Endpoints */}
            <div>
              <h3 className="font-semibold text-lg mb-2 text-purple-700">Employee Import Endpoints (NEW)</h3>
              <p className="text-slate-600 mb-3">Import employee data with full employment history, education, addresses and references from any HRMIS/CRM system.</p>
              
              <div className="space-y-4">
                <div className="border border-purple-200 rounded-lg p-4 bg-purple-50/30">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-green-600">POST</Badge>
                    <code className="font-semibold">/employees</code>
                  </div>
                  <p className="text-slate-600 mb-2">Import single employee with full data</p>
                  <pre className="bg-white border p-3 rounded text-xs overflow-x-auto">{`{
  "name": "John Smith",
  "email": "john@company.com",
  "external_reference_id": "EMP_001",
  "external_system": "zoho_creator",
  "entity_id": "your-entity-id",
  "father_name": "James Smith",
  "dob": "1990-05-15",
  "phone": "9876543210",
  "employee_category": "experienced",
  "pan_number": "ABCDE1234F",
  "aadhar_number": "123456789012",
  "employment_entries": [
    {
      "employer_name": "Previous Corp",
      "designation": "Software Engineer",
      "date_of_joining": "2020-01-15",
      "date_of_relieving": "2023-06-30",
      "hr_email": "hr@previouscorp.com",
      "is_current": false
    },
    {
      "employer_name": "Current Inc",
      "designation": "Senior Engineer",
      "date_of_joining": "2023-07-01",
      "is_current": true
    }
  ],
  "education_entries": [
    {
      "education_type": "graduation",
      "institution_name": "ABC University",
      "course": "B.Tech",
      "specialization": "Computer Science",
      "year_of_passing": "2019"
    }
  ],
  "addresses": [
    {
      "address_type": "present",
      "house_no": "123",
      "area": "Tech Park",
      "city": "Bangalore",
      "state": "Karnataka",
      "pincode": "560001"
    }
  ],
  "auto_initiate_verification": true,
  "verification_checks": ["address_pv", "employment_pv", "education_pv"]
}`}</pre>
                </div>
                
                <div className="border border-purple-200 rounded-lg p-4 bg-purple-50/30">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-green-600">POST</Badge>
                    <code className="font-semibold">/employees/bulk</code>
                  </div>
                  <p className="text-slate-600 mb-2">Bulk import multiple employees at once</p>
                  <pre className="bg-white border p-3 rounded text-xs overflow-x-auto">{`{
  "batch_reference": "ZOHO_BATCH_001",
  "default_entity_id": "your-entity-id",
  "employees": [
    { "name": "Employee 1", "email": "emp1@company.com", ... },
    { "name": "Employee 2", "email": "emp2@company.com", ... }
  ]
}`}</pre>
                </div>
                
                <div className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-blue-600">GET</Badge>
                    <code>/employees/reference/{'{external_reference_id}'}</code>
                  </div>
                  <p className="text-slate-600">Get employee by your HRMIS Employee ID</p>
                </div>
                
                <div className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-blue-600">GET</Badge>
                    <code>/employees/{'{employee_id}'}</code>
                  </div>
                  <p className="text-slate-600">Get employee details and associated verification cases</p>
                </div>
                
                <div className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-blue-600">GET</Badge>
                    <code>/employees?skip=0&limit=50</code>
                  </div>
                  <p className="text-slate-600">List all imported employees with pagination</p>
                </div>
              </div>
            </div>
            
            {/* Data Structures */}
            <div>
              <h3 className="font-semibold text-lg mb-2">Data Structures</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="border rounded-lg p-3">
                  <h4 className="font-medium text-purple-700 mb-2">Employment Entry</h4>
                  <ul className="text-xs text-slate-600 space-y-1">
                    <li>• employer_name (required)</li>
                    <li>• designation</li>
                    <li>• employee_code</li>
                    <li>• date_of_joining / date_of_relieving</li>
                    <li>• hr_name / hr_email / hr_phone</li>
                    <li>• rm_name / rm_email (Reporting Manager)</li>
                    <li>• salary, reason_for_leaving</li>
                    <li>• is_current (boolean)</li>
                  </ul>
                </div>
                
                <div className="border rounded-lg p-3">
                  <h4 className="font-medium text-purple-700 mb-2">Education Entry</h4>
                  <ul className="text-xs text-slate-600 space-y-1">
                    <li>• education_type (10th, 12th, graduation, post_graduation, diploma)</li>
                    <li>• institution_name (required)</li>
                    <li>• university_name</li>
                    <li>• course / specialization</li>
                    <li>• year_of_passing / roll_number</li>
                    <li>• percentage_cgpa</li>
                    <li>• mode_of_study (regular, distance)</li>
                  </ul>
                </div>
                
                <div className="border rounded-lg p-3">
                  <h4 className="font-medium text-purple-700 mb-2">Address Entry</h4>
                  <ul className="text-xs text-slate-600 space-y-1">
                    <li>• address_type (present, permanent, previous)</li>
                    <li>• house_no, street, area</li>
                    <li>• city, state, pincode</li>
                    <li>• period_of_stay</li>
                    <li>• residence_type (own, rented, parental)</li>
                  </ul>
                </div>
                
                <div className="border rounded-lg p-3">
                  <h4 className="font-medium text-purple-700 mb-2">Reference Entry</h4>
                  <ul className="text-xs text-slate-600 space-y-1">
                    <li>• reference_type (personal, professional)</li>
                    <li>• name (required)</li>
                    <li>• relationship / designation</li>
                    <li>• phone / email</li>
                    <li>• company, known_since</li>
                  </ul>
                </div>
              </div>
            </div>
            
            {/* Case Endpoints */}
            <div>
              <h3 className="font-semibold text-lg mb-2">Case Endpoints (B2B)</h3>
              
              <div className="space-y-4">
                <div className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-green-600">POST</Badge>
                    <code>/cases</code>
                  </div>
                  <p className="text-slate-600 mb-2">Create a new verification case</p>
                  <pre className="bg-slate-100 p-3 rounded text-xs overflow-x-auto">{`{
  "candidate_name": "John Smith",
  "check_type": "address_pv",
  "phone": "9876543210",
  "address": "123 Main St",
  "city": "Mumbai",
  "external_reference_id": "YOUR_SYSTEM_ID"
}`}</pre>
                </div>
                
                <div className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-blue-600">GET</Badge>
                    <code>/cases/reference/{'{external_reference_id}'}</code>
                  </div>
                  <p className="text-slate-600">Get case status by your reference ID</p>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg mb-2">Check Types</h3>
              <div className="grid grid-cols-2 gap-2">
                {['address_pv', 'employment_pv', 'education_pv', 'identity_pv', 'reference_pv', 'criminal_pv', 'court_pv', 'credit_pv'].map(type => (
                  <code key={type} className="bg-slate-100 px-2 py-1 rounded text-xs">{type}</code>
                ))}
              </div>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg mb-2">Webhook Payload</h3>
              <pre className="bg-slate-100 p-3 rounded text-xs overflow-x-auto">{`{
  "event_type": "employee.imported",
  "timestamp": "2026-02-23T10:30:00Z",
  "case_id": "EMP_UUID",
  "external_reference_id": "YOUR_HRMIS_ID",
  "data": {
    "action": "created",
    "employee_id": "...",
    "case_id": "JV260223...",
    "email": "john@company.com"
  }
}`}</pre>
              <p className="text-xs text-slate-500 mt-2">
                Verify webhooks using the <code>X-Veecheck-Signature</code> header (HMAC-SHA256)
              </p>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-700 mb-2">Response Format</h4>
              <pre className="bg-white border p-3 rounded text-xs overflow-x-auto">{`{
  "success": true,
  "message": "Employee created successfully",
  "data": {
    "employee_id": "uuid",
    "action": "created",
    "external_reference_id": "EMP_001",
    "case_id": "JV260223ABC" // if auto_initiate_verification=true
  }
}`}</pre>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
